import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import { createEquipment, deleteEquipment, getEquipment, updateEquipment } from '@/services/hvl/equipment'
import type { IEquipmentFilters } from '@/types/hvl/filters'
import type { IEquipmentUpsertForm } from '@/types/hvl/forms'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

export const useEquipment = () => {
  const client = useQueryClient()

  const getEquipmentQuery = (filters: IEquipmentFilters) =>
    useQuery({
      queryKey: ['equipment', filters],
      queryFn: () => getEquipment(filters),
    })

  const deleteEquipmentMutation = useMutation({
    mutationFn: (id: number) => deleteEquipment(id),
    onError: NotifyErrorWrapper('Failed to delete equipment'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['equipment'] })
      NotifySuccess(`Equipment deleted successfully`)
    },
  })

  const createEquipmentMutation = useMutation({
    mutationFn: (form: IEquipmentUpsertForm) => createEquipment(form),
    onError: NotifyErrorWrapper('Failed to create equipment'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['equipment'] })
      NotifySuccess(`Equipment created successfully`)
    },
  })

  const updateEquipmentMutation = useMutation({
    mutationFn: ({ id, form }: { id: number; form: IEquipmentUpsertForm }) => updateEquipment(id, form),
    onError: NotifyErrorWrapper('Failed to update equipment'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['equipment'] })
      NotifySuccess(`Equipment updated successfully`)
    },
  })

  return {
    getEquipmentQuery,
    createEquipmentMutation,
    updateEquipmentMutation,
    deleteEquipmentMutation,
  }
}
